import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/app/_module/utils/show-toast"; 

import agencyQueryKeys from "../services/keys";
import {
  createAgency,
  deleteAgency,
  getAgencies,
  getAgencyById,
  updateAgency,
  getAgencyStats,
  addUserToAgency,
  createSubAgency,
  getSubAgencies,
  updateAgencyUserRole,
  getAgencyUsers,
  deleteAgencyUser,
  getPerformanceTrends,
  getSubAgencyStats,
  getSubAgencyTreeStats,
  getSubAgentStats,
  getAgentStats,
  getIndividualUserStats,
  cancelInvite,
  getPremiumByCarrier,
  getDialEfficiency,
  getAgencyPolicyStatsByState,
  getAgencyLeadStatsByState,
  moveUser,
  moveUsersBulk,
  getDisableEligibility,
  requestDeactivation,
  suspendAgent,
  unsuspendAgent,
  restoreAgent,
} from '../services/services';
import type {
  CreateAgencyRequest,
  UpdateAgencyRequest,
  AddUserToAgencyParams,
  GetSubAgenciesParams,
  UpdateAgencyUserRoleParams,
  DeleteAgencyUserParams,
  GetAgencyUsersResponse,
  MoveUserParams,
} from "../types/types";

// Type for agency filters
export interface AgencyFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

// Hook to get all agencies with optional filtering
export const useGetAgencies = (filters?: AgencyFilters) => {
  return useQuery({
    queryKey: agencyQueryKeys.list(filters),
    queryFn: () => getAgencies(),
    staleTime: 5 * 60 * 1000, // agency records rarely change; focus-refetch is enough
    refetchOnMount: true, // Ensure fresh data on mount
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get active agencies only
export const useGetActiveAgencies = () => {
  return useQuery({
    queryKey: agencyQueryKeys.list({ isActive: true }),
    queryFn: () => getAgencies(),
    staleTime: 5 * 60 * 1000, // agency records rarely change; focus-refetch is enough
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get inactive agencies only
export const useGetInactiveAgencies = () => {
  return useQuery({
    queryKey: agencyQueryKeys.list({ isActive: false }),
    queryFn: () => getAgencies(),
    staleTime: 5 * 60 * 1000, // agency records rarely change; focus-refetch is enough
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get single agency by ID
export const useAgency = (agencyId: string) => {
  return useQuery({
    queryKey: agencyQueryKeys.detail(agencyId),
    queryFn: () => getAgencyById(agencyId),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000, // agency records rarely change; focus-refetch is enough
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to create agency
export const useCreateAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgencyRequest) => createAgency(data),
    onSuccess: async () => {
      // Invalidate and refetch agencies lists
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: agencyQueryKeys.lists(),
        }),
      ]);
      showToast("Agency created successfully", "success");
    },
    onError: () => {
      showToast("Failed to create agency", "error");
    },
  });
};

// Hook to create sub agency
export const useCreateSubAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgencyRequest) => createSubAgency(data),
    onSuccess: async (result, variables) => {
      // Invalidate multiple query patterns to ensure UI updates
      await Promise.all([
        // Sub-agencies queries - use partial key to match ALL subAgencies queries regardless of filters
        queryClient.invalidateQueries({ queryKey: ["agencies", "subAgencies"], exact: false }),
        // Parent agency details (to update sub-agency count)
        queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(variables.parentAgencyId || '') }),
        // All agencies list (in case this affects hierarchy)
        queryClient.invalidateQueries({ queryKey: agencyQueryKeys.lists() }),
        // Agency hierarchy if it exists
        queryClient.invalidateQueries({ queryKey: ["agencies", "hierarchy"], exact: false }),
      ]);
      
      // Toast is handled by the calling component (CreateSubAgencyModal) for richer context
    },
    onError: () => {
      showToast("Failed to create sub agency", "error");
    },
  });
};

// Hook to update agency
export const useUpdateAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agencyId,
      data,
    }: {
      agencyId: string;
      data: UpdateAgencyRequest;
    }) => updateAgency(agencyId, data),
    onSuccess: async (_, { agencyId }) => {
      // Invalidate and refetch agencies lists, sub-agencies, and specific agency
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(agencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.subAgencies() }),
          queryClient.invalidateQueries({ queryKey: ["agencies", "subAgencies"], exact: false }),
        ]);
      } catch (err) {

      }
      showToast("Agency updated successfully", "success");
    },
    onError: () => {
      showToast("Failed to update agency", "error");
    },
  });
};

// Hook to delete agency
export const useDeleteAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agencyId: string) => deleteAgency(agencyId),
    onSuccess: async () => {
      // Invalidate and refetch agencies lists
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: agencyQueryKeys.lists(),
        }),
      ]);
      showToast("Agency deleted successfully", "success");
    },
    onError: () => {
      showToast("Failed to delete agency", "error");
    },
  });
};

// Hook to get agency stats
export const useAgencyStats = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string
) => {
  // Create a stable query key that includes custom date range if provided
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: [...agencyQueryKeys.statsDetail(agencyId, period), ...queryKeyExtras],
    queryFn: () => getAgencyStats({ agencyId, period, startDate, endDate }),
    enabled: !!agencyId,
    staleTime: 60 * 1000, // 1 minute — keeps the dashboard feeling live without
    refetchInterval: 60 * 1000, // pounding the backend when the user sits idle.
    refetchIntervalInBackground: false, // pause polling when the tab is hidden — no
    refetchOnMount: true, // point paying for queries a user can't see anyway.
    refetchOnWindowFocus: true, // Snap refresh when they tab back in.
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to add user to agency
export const useAddUserToAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddUserToAgencyParams) => addUserToAgency(params),
    onSuccess: async (_, { agencyId }) => {
      // Invalidate and refetch agency details and users
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(agencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.addUser(agencyId) }),
        ]);
      } catch (err) {

      }
    },
    onError: () => {
      showToast("Failed to add user to agency", "error");
    },
  });
};

// Hook to get sub agencies
export const useGetSubAgencies = (params?: GetSubAgenciesParams) => {
  return useQuery({
    queryKey: agencyQueryKeys.subAgencies(params),
    queryFn: () => getSubAgencies(params),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to update agency user role
export const useUpdateAgencyUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAgencyUserRoleParams) => updateAgencyUserRole(params),
    onSuccess: async (_, { agencyId }) => {
      // Invalidate and refetch agency users
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(agencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(agencyId) }),
          queryClient.invalidateQueries({ queryKey: ["agencies", "subAgencies"], exact: false }),
        ]);
      } catch (err) {

      }
    },
    onError: () => {
      showToast("Failed to update user role", "error");
    },
  });
};

// Hook to get agency users. includeSubAgencies=true returns the whole
// descendant hierarchy in one list (each agent tagged with their agency).
export const useGetAgencyUsers = (agencyId: string | null, includeSubAgencies?: boolean) => {
  return useQuery<GetAgencyUsersResponse>({
    queryKey: [...agencyQueryKeys.agencyUsers(agencyId || ''), includeSubAgencies ? 'hierarchy' : 'single'],
    queryFn: async () => {
      try {
        // Note: axios interceptor unwraps response.data, so we get GetAgencyUsersResponse directly
        return await getAgencyUsers(agencyId!, includeSubAgencies) as unknown as GetAgencyUsersResponse;
      } catch (error: any) {
        // Handle 404 errors gracefully - endpoint not implemented yet
        if (error?.response?.status === 404) {
          return { users: [], data: [], total: 0, page: 1, limit: 10, totalPages: 0, pendingInvites: [] } as GetAgencyUsersResponse;
        }

        throw error;
      }
    },
    enabled: !!agencyId && agencyId.trim().length > 0,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // keep the dashboard live without a manual refresh
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Ensure fresh data on mount
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (endpoint not implemented)
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Disable outright vs request — fetched when the remove dialog opens
export const useDisableEligibility = (agencyId?: string, userId?: string) =>
  useQuery({
    queryKey: ["agency", agencyId, "disable-eligibility", userId],
    queryFn: () => getDisableEligibility(agencyId as string, userId as string),
    enabled: !!agencyId && !!userId,
    staleTime: 0,
  });

export const useRequestDeactivation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { agencyId: string; userId: string; reason?: string }) => requestDeactivation(params),
    onSuccess: async (_, { agencyId, userId }) => {
      await queryClient.invalidateQueries({ queryKey: ["agency", agencyId, "disable-eligibility", userId] });
    },
  });
};

/** Suspend / unsuspend / restore. All three refresh the roster so the row recolours. */
const useAgentLockAction = (
  fn: (agencyId: string, userId: string, reason?: string, autoDisableAfterDays?: number | null) => Promise<any>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (p: { agencyId: string; userId: string; reason?: string; autoDisableAfterDays?: number | null }) =>
      fn(p.agencyId, p.userId, p.reason, p.autoDisableAfterDays),
    onSuccess: async (_, { agencyId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(agencyId) }),
        queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(agencyId) }),
      ]).catch(() => undefined);
    },
  });
};

export const useSuspendAgent = () => useAgentLockAction(suspendAgent);
export const useUnsuspendAgent = () => useAgentLockAction(unsuspendAgent);
export const useRestoreAgent = () => useAgentLockAction(restoreAgent);

// Hook to delete agency user
export const useDeleteAgencyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DeleteAgencyUserParams) => deleteAgencyUser(params),
    onSuccess: async (_, { agencyId, data }) => {
      // Invalidate and refetch agency users
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(agencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(agencyId) }),
        ]);
      } catch (err) {

      }
      showToast(data.disableAccount ? "Account disabled and removed from agency" : "User removed from agency successfully", "success");
    },
    onError: (error: any) => {
      // the backend's reason ("primary agency is elsewhere", "owners only") beats a generic line
      showToast(error?.message || "Failed to remove user from agency", "error");
    },
  });
};

// Hook to move user between agencies (with data transfer)
export const useMoveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: MoveUserParams) => moveUser(params),
    onSuccess: async (_, { sourceAgencyId, data }) => {
      // Invalidate both source and target agency data
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(sourceAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(sourceAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(data.targetAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(data.targetAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.subAgencies() }),
        ]);
      } catch (err) {
        // silently ignore invalidation errors
      }
    },
    onError: () => {
      showToast("Failed to move user", "error");
    },
  });
};

// Hook to bulk-move several agents at once
export const useMoveUsersBulk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sourceAgencyId: string; targetAgencyId: string; userIds: string[]; role?: string }) =>
      moveUsersBulk(params),
    onSuccess: async (_res, { sourceAgencyId, targetAgencyId }) => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(sourceAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(sourceAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(targetAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.detail(targetAgencyId) }),
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.subAgencies() }),
        ]);
      } catch {
        // ignore invalidation errors
      }
    },
    onError: () => {
      showToast("Failed to move agents", "error");
    },
  });
};

export const useGetPerformanceTrends = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: [...agencyQueryKeys.performanceTrends(agencyId, period), ...queryKeyExtras],
    queryFn: () => getPerformanceTrends(agencyId, period, startDate, endDate),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get sub-agency stats
export const useGetSubAgencyStats = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string,
  dryRun = false
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: [...agencyQueryKeys.subAgencyStats(agencyId, period), ...queryKeyExtras],
    queryFn: () => getSubAgencyStats({ agencyId, period, startDate, endDate, dryRun }),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get the full sub-agency TREE with subtree-rolled-up stats.
export const useGetSubAgencyTree = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string,
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ['agencies', 'subAgencyTree', agencyId, period, ...queryKeyExtras],
    queryFn: () => getSubAgencyTreeStats({ agencyId, period, startDate, endDate }),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

// Hook to get sub-agent stats
// Get agent stats (individual agent performance within an agency)
export const useGetAgentStats = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string,
  dryRun = false
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "agents", "stats", period, ...queryKeyExtras],
    queryFn: () => getAgentStats({ agencyId, period, startDate, endDate, dryRun }),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useGetSubAgentStats = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months'
) => {
  return useQuery({
    queryKey: agencyQueryKeys.subAgentStats(agencyId, period),
    queryFn: () => getSubAgentStats({ agencyId, period }),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // keep the dashboard live without a manual refresh
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get individual user stats (for any agency member - agent, owner, admin)
export const useGetIndividualUserStats = (
  agencyId: string,
  userId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string,
) => {
  // Custom date range must be part of the query key or React Query treats
  // a date change as the same query and serves stale data — this was the
  // "middle of the page doesn't update when I filter" symptom.
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "user", userId, "stats", period, ...queryKeyExtras],
    queryFn: () => getIndividualUserStats({ agencyId, userId, period, startDate, endDate }),
    enabled: !!agencyId && !!userId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to cancel a pending invite
export const useCancelInvite = (agencyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => cancelInvite(inviteId),
    onSuccess: async () => {
      // Invalidate agency users queries to refresh pending invites list
      await Promise.all([
        // Invalidate the specific agency users query if agencyId is provided
        agencyId && queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(agencyId) }),
        // Also invalidate all agencyUsers queries (matches ["agencies", "agencyUsers", ...])
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === 'agencies' && key[1] === 'agencyUsers';
          }
        }),
        // Invalidate agency lists for good measure
        queryClient.invalidateQueries({ queryKey: agencyQueryKeys.lists() }),
      ]);
      showToast("Invite cancelled successfully", "success");
    },
    onError: () => {
      showToast("Failed to cancel invite", "error");
    },
  });
};

// Hook to get premium breakdown by carrier for pie chart
export const useGetPremiumByCarrier = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "premium-by-carrier", period, ...queryKeyExtras],
    queryFn: () => getPremiumByCarrier({ agencyId, period, startDate, endDate }),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook for the Dial Efficiency (call-effort economics) analytics tab
export const useGetDialEfficiency = (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string,
  agentId?: string
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "dial-efficiency", period, agentId ?? "all", ...queryKeyExtras],
    queryFn: () => getDialEfficiency({ agencyId, period, startDate, endDate, agentId }),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get policy statistics by state for agency policy map
export const useGetAgencyPolicyStatsByState = (
  agencyId: string,
  period: string = '6-months',
  startDate?: string,
  endDate?: string,
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "policy-stats-by-state", period, ...queryKeyExtras],
    queryFn: () => getAgencyPolicyStatsByState(agencyId, period, startDate, endDate),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

// Hook to get lead statistics by state for agency lead map
export const useGetAgencyLeadStatsByState = (
  agencyId: string,
  period: string = '6-months',
  startDate?: string,
  endDate?: string,
) => {
  const queryKeyExtras = startDate && endDate ? [startDate, endDate] : [];
  return useQuery({
    queryKey: ["agencies", agencyId, "lead-stats-by-state", period, ...queryKeyExtras],
    queryFn: () => getAgencyLeadStatsByState(agencyId, period, startDate, endDate),
    enabled: !!agencyId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 2,
  });
};
